import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';

// Models
import { CmsEntities } from 'client/data/models/cms';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { TypeRankingsModel, buildSubTypeRankingsPath } from 'client/data/models/type-rankings';

// Utils
import { TrackingHandler } from 'client/tracking/handler';
import { getBreadcrumbName, getUrlSlugByName } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { scrollToHash } from 'client/utils/scroll';
import { getCustomSubtypeTitle } from 'client/site-modules/upper-funnel/utils/seo-content';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { getSubtypeDisplayTitle } from 'site-modules/upper-funnel/utils/subtype-display';
import { hasMostSoldTypeContent } from 'site-modules/shared/utils/upper-funnel/type-rankings';

// Constants
import { TrackingMap } from 'client/tracking/maps/upper-funnel/type/type';

// Shared components
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';

// Components
import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { VehicleTypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-type-rankings-list/vehicle-type-rankings-list';
import { AnchorNavDropdown } from 'site-modules/upper-funnel/components/type/anchor-nav/anchor-nav-dropdown';
import { MostSoldTypeInfographic } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/most-sold-type-infographic';
import { HeroContent } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';
import { getSubtypeOverflowAdPos, getIndicesLength } from './get-subtype-overflow-ad-pos';

import './subtype.scss';

const VehicleTypeRankingsListWithCompareTray = withCompareVehicleTray(VehicleTypeRankingsList, {
  backFocusLink: {
    label: 'Skip to vehicle rankings',
    selector: '.vehicle-card .compare-checkbox input',
  },
});

export const mappedIndicesForSubtypeAdPlacement = vehicleEditorialRankings =>
  vehicleEditorialRankings.reduce((acc, subtype, index) => {
    if (index > 0) {
      const prevTotalNum = vehicleEditorialRankings[index - 1].segmentRatings.length;
      const prevAcc = acc[index - 1].startIndex;
      acc.push({
        startIndex: prevTotalNum + prevAcc,
        endIndex: prevTotalNum + prevAcc + vehicleEditorialRankings[index].segmentRatings.length,
      });
    } else {
      acc.push({
        startIndex: 0,
        endIndex: get(vehicleEditorialRankings[0], 'segmentRatings', []).length,
      });
    }
    return acc;
  }, []);

export function SubtypePageUI(props) {
  const { content, type, subtype, isMobile, referer, vehicleEditorialRankings, location } = props;

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);

    scrollToHash(get(location, 'hash', ''), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRefererPathName = () => (referer ? get(referer.match(/edmunds.com(.*)/), '[1]', '') : '');
  const description = content.metadata('description').value();
  const breadCrumbData = [
    getHomeBreadcrumb(),
    {
      title: getBreadcrumbName(type),
      href: `/${getUrlSlugByName(type)}`,
    },
    {
      title: subtype.replace(/suvs/g, 'SUVs'),
    },
  ];
  const trkParam = getRefererPathName(referer) === '/' ? { trk: type } : {};
  const title = getCustomSubtypeTitle(type, subtype);

  //   Most Sold Type Infographic
  const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
  const mostSoldTypeUniqueNameSingular = mostSoldTypeUniqueName.substring(0, mostSoldTypeUniqueName.length - 1);
  const mostSoldTypeGraphTitle = hasMostSoldTypeContent(type, subtype)
    ? `Most Popular ${getSubtypeDisplayTitle(type, subtype)}`
    : null;

  const subheadTitle = getSubtypeDisplayTitle(type, subtype, true);

  const subtypeAdPlacementIndices = mappedIndicesForSubtypeAdPlacement(vehicleEditorialRankings);

  return (
    <main id="main-content" className="type-page-v2 subtype-page-v2" role="main" aria-labelledby="type-page-heading">
      <HeroContent
        breadcrumbsData={breadCrumbData}
        isMobile={isMobile}
        type={type}
        title={title}
        isChildPage
        subtypeTitle={subheadTitle}
      />
      <Container className="mb-2">
        <TopRatedPromoUnit className="my-2" isMobile={isMobile} />
        <Row data-tracking-parent="edm-entry-summary">
          <Col xs={12} className="pt-sm-1_5 pb-1_25 px-0_5 px-sm-0">
            <AnchorNavDropdown
              vehicleEditorialRankings={vehicleEditorialRankings}
              type={mostSoldTypeUniqueNameSingular}
              mostSoldTypeGraphTitle={mostSoldTypeGraphTitle}
              isMobile={isMobile}
              hasSeparator={false}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <VehicleTypeRankingsListWithCompareTray
              type={type}
              trkParam={trkParam}
              vehicleEditorialRankings={vehicleEditorialRankings}
              isSubtypePage
              subtypeAdPlacementIndices={subtypeAdPlacementIndices}
            />
          </Col>
          <Col xs={12} className="d-flex flex-column">
            {getSubtypeOverflowAdPos(getIndicesLength(subtypeAdPlacementIndices), trkParam, isMobile)}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {mostSoldTypeGraphTitle && (
              <ScrollElement id={`best-selling-${mostSoldTypeUniqueName}`}>
                <RenderWhenViewable
                  verticalOffset="100%"
                  placeholder={
                    <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} showMap={false} />
                  }
                >
                  <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} />
                </RenderWhenViewable>
              </ScrollElement>
            )}
            {description && (
              <ContentFragment classes="text-gray-darker pb-3 description">{description}</ContentFragment>
            )}
          </Col>
        </Row>
      </Container>
    </main>
  );
}

SubtypePageUI.propTypes = {
  type: PropTypes.string.isRequired,
  subtype: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  vehicleEditorialRankings: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  referer: PropTypes.string,
};

SubtypePageUI.defaultProps = {
  content: null,
  isMobile: false,
  vehicleEditorialRankings: [],
  referer: '',
};

export const stateToPropsConfig = {
  vehicleEditorialRankings: bindToPath(
    ({ type, subtype }) => buildSubTypeRankingsPath(`${type},${subtype}`, subtype),
    TypeRankingsModel
  ),
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  referer: get(state, 'pageContext.page.referer'),
  location: get(state, 'pageContext.location'),
});

export const SubtypePage = connect(mapStateToProps)(connectToModel(SubtypePageUI, stateToPropsConfig));
